/**
 * @fileoverview Shared agent request validators for identity, runtime config,
 * permissions, instruction bundles, icons, and runtime diagnostics.
 *
 * @see doc/product/domains/agents/identity-config.md - agent config and status model
 * @see doc/product/domains/agents/instruction-loading.md - instruction bundle payloads
 * @see doc/product/domains/agents/skills-and-inbox.md - skill and inbox-facing agent surfaces
 */
import { z } from "zod";
import {
  AGENT_AVATAR_BACKGROUND_PRESET_IDS,
  AGENT_DICEBEAR_NOTIONISTS_ICON_PREFIX,
  AGENT_ICON_NAMES,
  AGENT_OREO_ICON_PREFIX,
  AGENT_OREO_PALETTE_IDS,
  AGENT_OREO_SHAPE_IDS,
  AGENT_ROLES,
  AGENT_RUNTIME_TYPES,
  AGENT_STATUSES,
} from "../constants.js";
import { validateModelFallbacksConfig } from "./model-fallbacks.js";
import { envConfigSchema } from "./secret.js";

export const agentPermissionsSchema = z.object({
  canCreateAgents: z.boolean().optional().default(true),
  canManageSkills: z.boolean().optional().default(true),
});

export const agentInstructionsBundleModeSchema = z.enum(["managed", "external"]);

export const updateAgentInstructionsBundleSchema = z.object({
  mode: agentInstructionsBundleModeSchema.optional(),
  rootPath: z.string().trim().min(1).nullable().optional(),
  entryFile: z.string().trim().min(1).optional(),
  clearLegacyPromptTemplate: z.boolean().optional().default(false),
});

export type UpdateAgentInstructionsBundle = z.infer<typeof updateAgentInstructionsBundleSchema>;

export const upsertAgentInstructionsFileSchema = z.object({
  path: z.string().trim().min(1),
  content: z.string(),
  clearLegacyPromptTemplate: z.boolean().optional().default(false),
});

export type UpsertAgentInstructionsFile = z.infer<typeof upsertAgentInstructionsFileSchema>;

const agentRuntimeConfigSchema = z.record(z.unknown()).superRefine((value, ctx) => {
  const envValue = value.env;
  if (envValue !== undefined) {
    const parsed = envConfigSchema.safeParse(envValue);
    if (!parsed.success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "agentRuntimeConfig.env must be a map of valid env bindings",
        path: ["env"],
      });
    }
  }

  validateModelFallbacksConfig(value, ctx, []);
});

const optionalAgentNameSchema = z.preprocess(
  (value) => {
    if (typeof value !== "string") return value;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  },
  z.string().trim().min(1).optional(),
);

export const uploadedAgentIconSchema = z.string().regex(
  new RegExp(
    "^asset:[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}"
      + `(?:\\?bg=(?:${AGENT_AVATAR_BACKGROUND_PRESET_IDS.join("|")}))?$`,
    "i",
  ),
  "Invalid uploaded avatar reference",
);

export const diceBearNotionistsAgentIconSchema = z.string().regex(
  new RegExp(
    `^${AGENT_DICEBEAR_NOTIONISTS_ICON_PREFIX}`
      + "[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}"
      + `(?:\\?bg=(?:${AGENT_AVATAR_BACKGROUND_PRESET_IDS.join("|")}))?$`,
    "i",
  ),
  "Invalid DiceBear avatar reference",
);

export const oreoAgentIconSchema = z.string().regex(
  new RegExp(
    `^${AGENT_OREO_ICON_PREFIX}`
      + `(?:${AGENT_OREO_SHAPE_IDS.join("|")}):`
      + `(?:${AGENT_OREO_PALETTE_IDS.join("|")}):`
      + "[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$",
    "i",
  ),
  "Invalid Oreo avatar reference",
);

export const agentIconSchema = z.preprocess(
  (value) => {
    if (typeof value !== "string") return value;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  },
  z.union([
    z.enum(AGENT_ICON_NAMES),
    oreoAgentIconSchema,
    uploadedAgentIconSchema,
    diceBearNotionistsAgentIconSchema,
  ]).nullable(),
);

export const createAgentSchema = z.object({
  name: optionalAgentNameSchema,
  role: z.enum(AGENT_ROLES).optional().default("general"),
  title: z.string().optional().nullable(),
  icon: agentIconSchema.optional(),
  capabilities: z.string().optional().nullable(),
  desiredSkills: z.array(z.string().min(1)).optional(),
  agentRuntimeType: z.enum(AGENT_RUNTIME_TYPES).optional().default("process"),
  agentRuntimeConfig: agentRuntimeConfigSchema.optional().default({}),
  runtimeConfig: z.record(z.unknown()).optional().default({}),
  budgetMonthlyCents: z.number().int().nonnegative().optional().default(0),
  permissions: agentPermissionsSchema.optional(),
  metadata: z.record(z.unknown()).optional().nullable(),
  seedOrganizationIntelligenceDefaults: z.boolean().optional().default(false),
});

export type CreateAgent = z.infer<typeof createAgentSchema>;

export const createAgentHireSchema = createAgentSchema
  .omit({ seedOrganizationIntelligenceDefaults: true })
  .extend({
    sourceIssueId: z.string().uuid().optional().nullable(),
    sourceIssueIds: z.array(z.string().uuid()).optional(),
  });

export type CreateAgentHire = z.infer<typeof createAgentHireSchema>;

export const updateAgentSchema = createAgentSchema
  .omit({ permissions: true, seedOrganizationIntelligenceDefaults: true })
  .partial()
  .extend({
    permissions: z.never().optional(),
    replaceAgentRuntimeConfig: z.boolean().optional(),
    status: z.enum(AGENT_STATUSES).optional(),
    spentMonthlyCents: z.number().int().nonnegative().optional(),
  });

export type UpdateAgent = z.infer<typeof updateAgentSchema>;

export const updateAgentInstructionsPathSchema = z.object({
  path: z.string().trim().min(1).nullable(),
  agentRuntimeConfigKey: z.string().trim().min(1).optional(),
});

export type UpdateAgentInstructionsPath = z.infer<typeof updateAgentInstructionsPathSchema>;

export const createAgentKeySchema = z.object({
  name: z.string().min(1).default("default"),
});

export type CreateAgentKey = z.infer<typeof createAgentKeySchema>;

export const wakeAgentSchema = z.object({
  source: z.enum(["timer", "assignment", "review", "on_demand", "automation"]).optional().default("on_demand"),
  triggerDetail: z.enum(["manual", "ping", "callback", "system"]).optional(),
  reason: z.string().optional().nullable(),
  payload: z.record(z.unknown()).optional().nullable(),
  idempotencyKey: z.string().optional().nullable(),
  forceFreshSession: z.preprocess(
    (value) => (value === null ? undefined : value),
    z.boolean().optional().default(false),
  ),
});

export type WakeAgent = z.infer<typeof wakeAgentSchema>;

const delegationTaskSchema = z.string().trim().min(1).refine(
  (value) => Array.from(value).length <= 20_000,
  "task must be no more than 20,000 Unicode characters",
);

export const createDelegationRunSchema = z.object({
  task: delegationTaskSchema,
  targetAgentId: z.string().trim().min(1).max(200).optional(),
  idempotencyKey: z.string().trim().min(1).max(500),
});

export type CreateDelegationRun = z.infer<typeof createDelegationRunSchema>;

export const resetAgentSessionSchema = z.object({
  taskKey: z.string().min(1).optional().nullable(),
});

export type ResetAgentSession = z.infer<typeof resetAgentSessionSchema>;

export const testAgentRuntimeEnvironmentSchema = z.object({
  agentRuntimeConfig: agentRuntimeConfigSchema.optional().default({}),
});

export type TestAgentRuntimeEnvironment = z.infer<typeof testAgentRuntimeEnvironmentSchema>;

export const updateAgentPermissionsSchema = z.object({
  canCreateAgents: z.boolean(),
  canManageSkills: z.boolean().optional(),
  canAssignTasks: z.boolean(),
});

export type UpdateAgentPermissions = z.infer<typeof updateAgentPermissionsSchema>;
